<template>
  <div>
    <p class="lh-150 mb-5 description">{{ $t('description') }}</p>
    <EditableText
      id="editable-text"
      @cancel="setDefaultName"
      @save="save"
      :allowSave="!$v.form.name.$invalid"
      :text="form.name"
      alignment="end"
      :customClass="['h3', 'font-weight-bold', 'lh-125']"
      :customClassIsEditing="['pb-2']"
    >
      <div>
        <label for="theme-name" class="mb-0 font-weight-bold custom-label">
          {{ $t('label.name') }}
        </label>
        <b-form-input id="theme-name" v-model="form.name" @blur="$v.form.name.$touch()" :state="!$v.form.name.$invalid">
        </b-form-input>
        <b-form-invalid-feedback class="position-absolute w-75 bg-white" :state="!$v.form.name.$invalid">
          {{ $t('name-error') }}
        </b-form-invalid-feedback>
      </div>
    </EditableText>
    <div :class="{ 'd-flex flex-row': theme }">
      <div
        v-if="theme"
        :class="{ 'theme-image w-50 mr-5': theme }"
        :style="`background-image: url(${theme.preview});`"
      />
      <div :class="{ 'w-50': theme }">
        <div :class="[$v.form.name.$invalid ? 'mt-4' : 'mt-2']">
          <label for="categories" class="custom-label lh-150 mb-0 font-weight-bold">{{
            $t('label.choose-category')
          }}</label>
          <div id="categories">
            <b-badge
              pill
              v-for="categ in availableCategories"
              :key="categ.value"
              class="font-weight-bold text-truncate"
              :class="form.category === categ.value ? 'active' : 'unselected-badge'"
              @click="onFilterClick(categ.value)"
            >
              {{ categ.text }}
            </b-badge>
          </div>
          <div class="pt-4">
            <label for="language" class="custom-label lh-150 mb-0 font-weight-bold">{{ $t('label.language') }}</label>
            <b-form-select id="language" v-model="form.locale" :options="languages" size="lg" />
          </div>
        </div>
      </div>
    </div>
    <div class="mt-4 pt-2 ">
      <label for="description" class="custom-label lh-150 mb-0 font-weight-bold">{{ $t('label.description') }}</label>
      <b-form-textarea id="description" v-model="form.description" />
      <span class="max-message lh-150 font-weight-normal">
        {{ $t('description-max-count', { count: 400 }) }}
      </span>
    </div>
    <div class="pt-5 d-flex justify-content-center">
      <b-button variant="outline-secondary" class="cancel-button font-weight-bold" @click="$emit('close')">{{
        $t('button.cancel')
      }}</b-button>
      <b-button
        variant="success"
        class="save-button font-weight-bold ml-3"
        @click="$emit('ok', form)"
        :disabled="$v.$invalid"
      >
        {{ theme ? $t('button.edit') : $t('button.save') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'

export default {
  i18nOptions: { keyPrefix: 'components.theme-modal.save-theme-confirmation' },
  components: {
    EditableText: () => import('@/components/EditableText'),
  },
  props: {
    theme: {
      type: Object,
    },
  },
  computed: {
    ...mapGetters('dashboard', ['categories', 'languages']),
    availableCategories() {
      return this.categories.filter(category => category.value !== 'all')
    },
    defaultName() {
      return this.$t('label.name')
    },
  },
  data() {
    return {
      form: {
        id: null,
        category: 'blank',
        description: null,
        name: null,
        locale: 'pt-BR',
      },
      tabIndex: 0,
    }
  },
  validations: {
    form: {
      description: {
        maxLength: maxLength(400),
      },
      name: {
        required,
      },
    },
  },
  methods: {
    mountForm() {
      if (this.theme) {
        this.form = { ...this.theme }
      }
    },
    onFilterClick(category) {
      this.form.category = category
    },
    save() {
      if (this.form.name === '') this.setDefaultName()
    },
    setDefaultName() {
      this.form.name = this.defaultName
    },
  },
  created() {
    this.setDefaultName()
    this.mountForm()
  },
}
</script>
<style lang="scss" scoped>
.description {
  font-size: 16px;
  color: #525252;
}
.custom-label {
  color: #262626;
}
/deep/ .badge {
  cursor: pointer;
  font-size: 14px !important;
  padding: 0.8em;
}
.theme-image {
  background-size: cover;
}
#categories {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}
.unselected-badge {
  background-color: #b7b8b9;
}
.active {
  background-color: $cherry;
}

#language {
  border: 1px solid #a8a8a8;
  color: #a8a8a8;
}

.max-message {
  font-size: 12px;
  color: #262626;
}
.cancel-button {
  border: 2px solid #a8a8a8;
  color: #a8a8a8;
  &:hover {
    color: #ffffff;
  }
}
.save-button {
  background-color: $cherry;
  border-color: $cherry;
  &:hover {
    background-color: $cherry-dark;
    border-color: $cherry-dark;
  }
  &:disabled {
    cursor: not-allowed;
  }
}
</style>
